const INTERFACE_PREFIX_RE = /^(?:PJSIP|Local|SIP|IAX2)\//;
const WEB_SUFFIX_RE = /-web$/i;

/** Extract the bare extension from an Asterisk interface / channel / member
 *  name. Paired WebRTC endpoints `NNNN-web` collapse onto `NNNN` so that the
 *  SIP softphone and the browser WebRTC tab are perceived as the SAME logical
 *  ramal in the UI. Mirror of normalizeExtension in callcenter-v2/shared. */
export function extractExtension(iface) {
  if (!iface) return null;
  const m = String(iface).match(
    /^(?:[A-Za-z][A-Za-z0-9]*\/)?(\d+)(?:-web)?(?:@[^/,]*)?(?:[/,].*)?$/i,
  );
  return m ? m[1] : null;
}

export function isPjsipInterface(iface) {
  return !!iface && String(iface).startsWith('PJSIP/');
}

/** True when the interface refers to the WebRTC twin of a base extension
 *  (e.g. `PJSIP/4001-web`). Used in the UI to render a "WebRTC" badge on
 *  forensic drill-down views so operators can tell which device answered. */
export function isWebEndpoint(iface) {
  if (!iface) return false;
  const s = String(iface).trim();
  if (!s) return false;
  const tail = s.includes('/') ? s.split('/').pop() : s;
  const head = tail.split('@')[0].split(',')[0];
  return WEB_SUFFIX_RE.test(head);
}

/** True when `name` is set and is NOT a raw Asterisk channel string like
 *  `"PJSIP/3530"` or `"Local/3530@mult-ring-rcr/n"`. Used to skip propagating
 *  Asterisk-default placeholders into agent display labels. */
export function isCleanName(name) {
  return !!name && !INTERFACE_PREFIX_RE.test(String(name));
}

const PLACEHOLDER_CALLER_VALUES = new Set(['s', '<s>', '<unknown>', 'unknown']);

function isPlaceholderCallerValue(value) {
  if (value == null) return true;
  const s = String(value).trim();
  if (!s) return true;
  return PLACEHOLDER_CALLER_VALUES.has(s.toLowerCase());
}

export function cleanCallerName(name) {
  if (isPlaceholderCallerValue(name)) return null;
  const s = String(name).trim();
  if (INTERFACE_PREFIX_RE.test(s)) return null;
  return s;
}

export function displayCallerNumber(number) {
  if (isPlaceholderCallerValue(number)) return null;
  return String(number).trim();
}

export function displayCallerName(name, number) {
  return cleanCallerName(name) || displayCallerNumber(number) || null;
}

export function displayRamal(agent) {
  if (!agent) return '--';
  return (
    agent.extension ||
    extractExtension(agent.interface) ||
    agent.interface ||
    '--'
  );
}

/** Resolves the AGENTE column label: cleaned displayName from DB, else cleaned
 *  memberName from Asterisk, else extension, else `--`. Never returns a value
 *  starting with `PJSIP/`/`Local/`/`SIP/`/`IAX2/`. */
export function displayAgentName(agent) {
  if (!agent) return '--';
  if (isCleanName(agent.displayName)) return agent.displayName;
  if (isCleanName(agent.memberName)) return agent.memberName;
  return agent.extension || extractExtension(agent.interface) || '--';
}

/** True when the agent has a usable identity (an interface containing `/` OR
 *  an extension). Filters out ghost entries from initial AMI QueueMember
 *  enumeration that have neither field populated. */
export function isUsefulAgent(agent) {
  if (!agent) return false;
  const iface = agent.interface || '';
  return iface.includes('/') || !!agent.extension;
}

/** Build a Set of agent extensions that have a connected assignment right now.
 *  Used to derive the "effective in-call" state because the AMI device-state
 *  flag `agent.inCall` is unreliable when the QueueMember is `Local/<ext>` (the
 *  Local channel is necessary for simultaneous SIP+WebRTC ringing but it never
 *  carries device state). Cross-referencing assignments gives a deterministic
 *  source of truth for "agent is on a call right now". */
export function extensionsInActiveAssignments(assignments) {
  const set = new Set();
  if (!assignments) return set;
  const list = Array.isArray(assignments) ? assignments : Object.values(assignments);
  for (const a of list) {
    if (!a || a.status !== 'connected') continue;
    const ext = a.extension || extractExtension(a.interface || '');
    if (ext) set.add(String(ext));
  }
  return set;
}

/** Pure, resolver-driven comparator for table sorting. Returns a NEW array
 *  (never sorts in place) so it is safe to call on memoized / state-derived
 *  lists. `resolvers` maps a sort key to a function producing the raw comparable
 *  value (a number or string — never the formatted `'—'` placeholder). Numbers
 *  compare numerically; everything else compares as a pt-BR locale string.
 *  `tiebreak` (optional) keeps order stable when primary values are equal —
 *  important because the source lists come from Redis-hash order, which
 *  reshuffles on reconnect. An unknown `key` returns the list unchanged. */
export function sortRows(list, key, dir, resolvers, tiebreak) {
  const get = resolvers[key];
  if (!get) return list;
  const sign = dir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    let c;
    if (typeof va === 'number' && typeof vb === 'number') c = va - vb;
    else c = String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
    if (c !== 0) return sign * c;
    return tiebreak
      ? String(tiebreak(a) ?? '').localeCompare(String(tiebreak(b) ?? ''), 'pt-BR')
      : 0;
  });
}

/** Strip the `{accountcode}-{tenant_slug}-` prefix from a full queue
 *  identifier for display. Falls back to a heuristic that strips just the
 *  accountcode segment when no prefix was supplied by the snapshot. */
export function displayQueue(fullName, namePrefix) {
  if (!fullName) return '--';
  const s = String(fullName);
  if (namePrefix && s.startsWith(namePrefix)) {
    return s.slice(namePrefix.length) || s;
  }
  // Fallback: strip the accountcode-* segment if it looks like one.
  const m = s.match(/^[a-z0-9]{2,5}-(.+)$/i);
  return m ? m[1] : s;
}

function commonPrefix(a, b) {
  const len = Math.min(a.length, b.length);
  let i = 0;
  while (i < len && a[i] === b[i]) i++;
  return a.slice(0, i);
}

/** Frontend port of the backend TenantQueuePrefixService.computePrefix. Derives
 *  the `{accountcode}-{tenant_slug}-` prefix from a set of full queue names so
 *  the History page can strip it even when the queues catalog is empty for the
 *  tenant (no namePrefix from the queues endpoint). */
export function computeQueuePrefix(accountcode, queueNames) {
  if (!accountcode) return null;
  const prefix = `${accountcode}-`;
  const candidates = (queueNames ?? []).filter(
    (n) => typeof n === 'string' && n.length > prefix.length && n.startsWith(prefix),
  );
  if (candidates.length === 0) return null;

  let lcp = candidates[0];
  for (let i = 1; i < candidates.length && lcp.length > 0; i++) {
    lcp = commonPrefix(lcp, candidates[i]);
  }

  const lastDash = lcp.lastIndexOf('-');
  if (lastDash < 0) return null;
  const trimmed = lcp.slice(0, lastDash + 1);
  if (!trimmed.startsWith(prefix)) return null;
  return trimmed;
}

export const QUEUE_STRATEGY_LABELS = {
  ringall: 'Múltiplo - Toca em todos',
  leastrecent: 'Tempo - Menos recente',
  fewestcalls: 'Chamada - Menos chamadas',
  random: 'Aleatório - Toca aleatoriamente',
  rrmemory: 'Rotativo - Continua de onde parou',
  linear: 'Sequencial - Ordem fixa',
  wrandom: 'Carteira - Aleatório ponderado',
  rrordered: 'Rotativo - Ordem do cadastro',
};

export function displayQueueStrategy(strategy) {
  if (!strategy) return '';
  return QUEUE_STRATEGY_LABELS[strategy] || strategy;
}
